import React, { FC, memo, useState, useEffect } from 'react';
import { IProductListFacets, IFacetTerm, IMontanaFacet } from '@montana/models/generated/cms.interface';
import { ButtonStyles, Button } from '@montana/shared/button';
import { useFilter } from '@montana/containers/filter';
import { SvgIcon } from '@montana/shared/svg-icon';
import { useTranslations } from '@montana/containers/translations';
import { MobileAndTabletLandscape, TabletLandscapeAndLarger } from '@montana/shared/responsive';
import { formatString } from '@montana/helpers/string-format/string-format.helper';
import { Accordion } from '@montana/shared/accordion';
import { SortingOption } from '@montana/models/dev';
import { IFieldOptionValue } from '@montana/shared/form/field/field';
import { Dropdown } from '@montana/shared/dropdown';
import { StrokeLine } from '@montana/shared/stroke';
import { getDepthTranslationValue, getFacetText } from '@montana/helpers/product.helper';
import { CUSTOMER_MODE } from '@montana/constants/customer-mode';
import { useSettings } from '@montana/containers/settings/settings.container';

export const FilterControls: FC = memo((): JSX.Element => {
    const {customerMode} = useSettings();
    const {
        applySingleFacet,
        removeSingleFacet,
        clearFilter,
        applySearch,
        applySorting,
        appliedFacets,
        facets,
        sortBy,
        meta,
        searchQuery
    } = useFilter();
    const [activeFacetGroup, setActiveFacetGroup] = useState<IProductListFacets | undefined>();
    const [showMobileFacetSelection, setShowMobileFacetSelection] = useState<boolean>(false);

    const translate = useTranslations();

    useEffect(() => {
        setActiveFacetGroup((prev: IProductListFacets | undefined) => facets?.find(facetGroup => facetGroup.facetName === prev?.facetName));
    }, [facets]);

    return (
        <section className="filter-controls">
            <div className="filter-nav">
                {customerMode != CUSTOMER_MODE.PROFESSIONAL && (
                    <div>
                        <MobileAndTabletLandscape>
                            <button className="filter-nav__mobile-button" onClick={() => setShowMobileFacetSelection(prev => !prev)}>
                                <SvgIcon className="filter-nav__mobile-button-icon" iconName={`${showMobileFacetSelection ? 'close' : 'burgernav'}`} />
                                <span className="filter-nav__mobile-button-text">{translate(t => t.filterAndSearch.filters, `[Filters]`)}</span>
                            </button>
                        </MobileAndTabletLandscape>
                        <TabletLandscapeAndLarger>
                            <div className="filter-nav__facet-links">
                                {facets?.filter((facetGroup: IProductListFacets) => !!facetGroup.terms?.length)
                                    .map((facetGroup: IProductListFacets) => (
                                        <Button
                                            key={`filter-nav__${facetGroup.facetName}`}
                                            onClick={() => setActiveFacetGroup(prev => prev === facetGroup ? undefined : facetGroup)}
                                            style={ButtonStyles.Foldable}
                                            inverseStrokeAnimation={true}
                                            strokeIsActive={activeFacetGroup?.facetName === facetGroup.facetName}
                                            foldableIsActive={activeFacetGroup?.facetName === facetGroup.facetName && activeFacetGroup !== undefined}
                                        >
                                            {translate(t => t.filterAndSearch[`facet${facetGroup.facetName}`], `[facet${facetGroup.facetName}]`)}
                                        </Button>
                                    ))}
                            </div>
                        </TabletLandscapeAndLarger>
                        <MobileAndTabletLandscape>
                            <div className="filter-nav__total-results-mobile">
                                {formatString(translate(t => t.filterAndSearch.itemsShowingShort, `[{0} items]`), meta?.hits || 0)}
                            </div>
                        </MobileAndTabletLandscape>
                    </div>
                )}
                {!showMobileFacetSelection && !!meta?.hits && (
                    <div className="filter-nav__sorting">
                        <Dropdown
                            label={translate((t) => t.filterAndSearch.sortBy, '[Sort by:]')}
                            options={Object.keys(SortingOption).map<IFieldOptionValue>((optionKey) => {
                                const option: string = SortingOption[optionKey as keyof typeof SortingOption];
                                return {
                                    value: option,
                                    text: translate(t => t.filterAndSearch[option], `[${option}]`),
                                }
                            })}
                            value={sortBy}
                            id="filter-controls__sorting-dropdown"
                            onChange={(value: SortingOption) => applySorting(value)}
                        />
                    </div>
                )}
            </div>

            <MobileAndTabletLandscape>
                <MobileFacetGroups active={showMobileFacetSelection} onCTACalled={() => setShowMobileFacetSelection(false)} />
            </MobileAndTabletLandscape>

            {/* On larger screen we show facets and chips */}
            <TabletLandscapeAndLarger>
                {activeFacetGroup && (
                    <FacetGroup
                        facetGroup={activeFacetGroup}
                        appliedFacets={appliedFacets}
                        onApply={applySingleFacet}
                        onRemove={removeSingleFacet} />
                )}
                {(!!appliedFacets?.length || !!searchQuery) && (
                    <div className={`filter-controls-applied ${activeFacetGroup ? 'filter-controls-applied--with-active-group' : ''}`}>
                        {!!searchQuery && (
                            <button
                                key={`facet-chip__search-${searchQuery}`}
                                className="facet-chip"
                                onClick={() => applySearch('')}
                            >
                                <SvgIcon className="facet-chip__svg" iconName="selected" />
                                <span className="facet-chip__text">{`"${searchQuery}"`}</span>
                            </button>
                        )}
                        {appliedFacets.map((facet: IMontanaFacet) => (
                            <button
                                key={`facet-chip__${facet.facetValue}`}
                                className="facet-chip"
                                onClick={() => removeSingleFacet(facet)}
                            >
                                <SvgIcon className="facet-chip__svg" iconName="selected" />
                                <span className="facet-chip__text">{getDepthTranslationValue(translate, facet.facetValue) ?? getFacetText(facet, facets)}</span>
                            </button>
                        ))}
                        <Button
                            onClick={() => clearFilter(customerMode)}
                            style={ButtonStyles.Text}
                            inverseStrokeAnimation={true}
                            className="facet-clear"
                        >
                            {translate((t) => t.filterAndSearch.clearAll, '[Clear all]')}
                        </Button>
                    </div>
                )}
            </TabletLandscapeAndLarger>
            <TabletLandscapeAndLarger>
                <div className="filter-nav__total-results">
                    {formatString(translate(t => t.filterAndSearch.itemsShowingFull, `[Showing {0} items]`), meta?.hits || 0)}
                </div>
            </TabletLandscapeAndLarger>
        </section>
    )
});

/**
 * MOBILE FACET GROUP CONTAINER
 */
interface IMobileFacetGroupsProps {
    active: boolean;
    onCTACalled: () => void;
}

const MobileFacetGroups: FC<IMobileFacetGroupsProps> = memo(({ active, onCTACalled }: IMobileFacetGroupsProps): JSX.Element => {
    const { appliedFacets, facets, applyFacets } = useFilter();
    const [disallowApply, setDisallowApply] = useState<boolean>(true);
    const [temporaryFacetFilters, setTemporaryFacetFilter] = useState<IMontanaFacet[]>([]);

    const translate = useTranslations();

    useEffect(() => {
        initTemporaryFacets();
    }, [appliedFacets]);

    /**
     * Set temporary facets to match currently applied facets held up against available facets.
     */
    const initTemporaryFacets = () => {
        setTemporaryFacetFilter(() => {
            let updatedFacetTerms: IMontanaFacet[] = [];
            facets?.forEach(facetGroup => {
                const facetTermsFromGroup = facetGroup?.terms?.filter((term: IFacetTerm) => appliedFacets.some(facet => facet.facetKey === facetGroup.facetName && facet.facetValue === term.text))
                    .map((term: IFacetTerm) => ({ facetKey: facetGroup.facetName, facetValue: term.text }));

                if (facetTermsFromGroup) {
                    updatedFacetTerms = [...updatedFacetTerms, ...facetTermsFromGroup];
                }
            });
            return updatedFacetTerms;
        });
        setDisallowApply(true);
    }

    const applyTemporaryFacet = (facet: IMontanaFacet) => {
        setTemporaryFacetFilter(prev => {
            const facetIndex = prev.indexOf(facet);
            return facetIndex < 0 ? [...prev, facet] : prev;
        });
        setDisallowApply(false);
    }

    const removeTemporaryFacet = (facet: IMontanaFacet) => {
        setTemporaryFacetFilter(prev => prev.filter(f => !(f.facetKey === facet.facetKey && f.facetValue === facet.facetValue)));
        setDisallowApply(false);
    }

    const handleCancel = () => {
        initTemporaryFacets();
        onCTACalled();
    }

    const handleApply = () => {
        applyFacets(temporaryFacetFilters);
        onCTACalled();
    }

    return (
        <>
            {active && (
                <div className="filter-controls-mobile__facet-groups">
                    {facets?.filter((facetGroup: IProductListFacets) => !!facetGroup.terms?.length)
                        .map((facetGroup: IProductListFacets) => (
                            <Accordion
                                key={`${facetGroup.facetName}__key`}
                                id={`${facetGroup.facetName}__key`}
                                className={'filter-controls-mobile__facet-group'}
                                header={({ handleButtonClick, ariaOpen, triggerID, contentID }) => (
                                    <button
                                        type="button"
                                        className="accordion__button filter-controls-mobile__facet-group__header"
                                        id={triggerID}
                                        aria-controls={contentID}
                                        aria-expanded={!ariaOpen}
                                        onClick={handleButtonClick}
                                    >
                                        <span>
                                            {translate(t => t.filterAndSearch[`facet${facetGroup.facetName}`], `[facet${facetGroup.facetName}]`)}
                                        </span>
                                        <StrokeLine active={!!ariaOpen} inverseAnimation={true}></StrokeLine>
                                    </button>
                                )}
                            >
                                <FacetGroup
                                    facetGroup={facetGroup}
                                    appliedFacets={temporaryFacetFilters}
                                    onApply={(facet: IMontanaFacet) => applyTemporaryFacet(facet)}
                                    onRemove={(facet: IMontanaFacet) => removeTemporaryFacet(facet)} />
                            </Accordion>
                        ))}
                    <div className="filter-controls-mobile__ctas">
                        <Button
                            className="filter-controls-mobile__apply"
                            disabled={disallowApply}
                            style={ButtonStyles.Primary}
                            onClick={handleApply}
                            text={translate((t) => t.filterAndSearch.apply, '[Apply]')}
                        />
                        <Button
                            className="filter-controls-mobile__cancel"
                            style={ButtonStyles.Secondary}
                            onClick={handleCancel}
                            text={translate((t) => t.general.cancel, '[Cancel]')}
                        />
                    </div>
                </div>
            )}
        </>
    );
});

/**
 * FACET GROUP
 */
interface IFacetGroupProps {
    facetGroup: IProductListFacets;
    appliedFacets: IMontanaFacet[];
    onApply: (facet: IMontanaFacet) => void;
    onRemove: (facet: IMontanaFacet) => void;
}

const FacetGroup: FC<IFacetGroupProps> = memo(({ facetGroup, appliedFacets, onApply, onRemove }: IFacetGroupProps) => (
    <div className={`filter-controls-group filter-controls-group${facetGroup.facetName === 'ColorValue' ? '--color' : '--text'}-facets`}>
        {facetGroup.terms?.map((facetTerm: IFacetTerm) => {
            const termAsFacet = { facetKey: facetGroup.facetName, facetValue: facetTerm.filterKey };
            const isApplied = appliedFacets.some((appliedFacet: IMontanaFacet) =>
                appliedFacet.facetKey === termAsFacet.facetKey && appliedFacet.facetValue === termAsFacet.facetValue);

            return (
                <TextFacet
                    key={`${facetGroup.facetName}__${facetTerm.filterKey}`}
                    active={isApplied}
                    facetTerm={facetTerm}
                    onClick={() => isApplied ? onRemove(termAsFacet) : onApply(termAsFacet)}
                />
            );
        })}
    </div>
))


/**
 * FACETS
 */
interface IFacetProps {
    active: boolean;
    onClick: () => void;
    facetTerm: IFacetTerm;
}

const TextFacet: FC<IFacetProps> = memo(({ active, onClick, facetTerm }: IFacetProps) => {
    const translate = useTranslations();

    return (
        <button onClick={onClick} className={`facet${active ? ' facet--active' : ''}`}>
            {active && <SvgIcon className="facet__active-indicator" iconName="selected" />}
            <div className="facet__content">
                <span className="facet__facet-label">
                    {`${getDepthTranslationValue(translate, facetTerm.text) ?? facetTerm.text}`}
                </span>
                <div className="facet__color-icons">
                    {facetTerm.colorIcons?.filter(icon => icon.hexcode).map((icon, index) => (
                        <div className="facet__color-icon" key={`color-icon__${icon.hexcode}__${index}`} style={{ backgroundColor: `#${icon.hexcode}` }}></div>
                    ))}
                </div>
            </div>
        </button>
    )
});
